import React, { useCallback, useEffect, useState } from "react";

import { useDrawolaT } from "../i18n";
import {
  createDoc,
  deleteDoc,
  getActiveDocId,
  listDocs,
  renameDoc,
  setActiveDocId,
} from "../multiFileStore";
import { formatMB, getSnapshot, isLowQuota } from "../quota";
import { LIMITS } from "../types";

import { StorageMeter } from "./StorageMeter";

import type { DocMeta, StorageSnapshot } from "../types";

type Props = {
  /** Called when user clicks a doc to switch to it. */
  onSwitch: (docId: string) => void;
  /** Called when user creates a new doc. */
  onCreate: (docId: string) => void;
  /** Called when the active doc is deleted (so parent can switch away). */
  onActiveDeleted: () => void;
  /** Current active doc id (from parent, so highlight stays in sync). */
  activeDocId: string | null;
  /** Bump this when parent wants us to re-read the list (after external save). */
  refreshToken?: number;
};

export const FileListSidebar: React.FC<Props> = ({
  onSwitch,
  onCreate,
  onActiveDeleted,
  activeDocId,
  refreshToken,
}) => {
  const t = useDrawolaT();
  const [docs, setDocs] = useState<DocMeta[] | null>(null);
  const [snapshot, setSnapshot] = useState<StorageSnapshot | null>(null);
  const [lowQuota, setLowQuota] = useState(false);

  const refresh = useCallback(async () => {
    const [list, snap, low] = await Promise.all([
      listDocs(),
      getSnapshot(),
      isLowQuota(),
    ]);
    setDocs(list);
    setSnapshot(snap);
    setLowQuota(low);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, refreshToken]);

  const handleNew = useCallback(async () => {
    if (snapshot && snapshot.docCount >= LIMITS.MAX_DOCUMENTS) {
      window.alert(t("files.atCapAlert", { max: LIMITS.MAX_DOCUMENTS }));
      return;
    }
    const defaultName = `${t(
      "files.untitledPrefix",
    )} ${new Date().toLocaleString()}`;
    const name = window.prompt(t("files.newPromptLabel"), defaultName) ?? "";
    if (!name.trim()) {
      return;
    }
    const meta = await createDoc(name.trim());
    await setActiveDocId(meta.id);
    onCreate(meta.id);
    refresh();
  }, [onCreate, refresh, snapshot, t]);

  const handleSwitch = useCallback(
    async (id: string) => {
      if (id === activeDocId) {
        return;
      }
      await setActiveDocId(id);
      onSwitch(id);
    },
    [activeDocId, onSwitch],
  );

  const handleRename = useCallback(
    async (id: string, currentName: string) => {
      const next = window.prompt(t("files.renamePromptLabel"), currentName);
      if (!next || !next.trim() || next === currentName) {
        return;
      }
      await renameDoc(id, next.trim());
      refresh();
    },
    [refresh, t],
  );

  const handleDelete = useCallback(
    async (id: string, name: string) => {
      if (!window.confirm(t("files.deleteConfirm", { name }))) {
        return;
      }
      const wasActive = id === (await getActiveDocId());
      await deleteDoc(id);
      if (wasActive) {
        onActiveDeleted();
      }
      refresh();
    },
    [onActiveDeleted, refresh, t],
  );

  const atCapCount = !!snapshot && snapshot.docCount >= LIMITS.MAX_DOCUMENTS;

  return (
    <div className="drawOla-file-list">
      <div className="drawOla-file-list__header">
        <h2>{t("files.title")}</h2>
        <button
          type="button"
          className="drawOla-file-list__new"
          onClick={handleNew}
          disabled={atCapCount}
          title={
            atCapCount
              ? t("files.atCapTitle", { max: LIMITS.MAX_DOCUMENTS })
              : t("files.newTitle")
          }
        >
          {t("files.new")}
        </button>
      </div>

      {lowQuota && (
        <div className="drawOla-file-list__low-quota">
          {t("files.lowQuotaBanner")}
        </div>
      )}

      {docs === null ? (
        <div className="drawOla-file-list__empty">{t("files.loading")}</div>
      ) : docs.length === 0 ? (
        <div className="drawOla-file-list__empty">{t("files.empty")}</div>
      ) : (
        <ul className="drawOla-file-list__items">
          {docs.map((d) => (
            <li
              key={d.id}
              className={`drawOla-file-list__item ${
                d.id === activeDocId ? "drawOla-file-list__item--active" : ""
              }`}
              onClick={() => handleSwitch(d.id)}
            >
              <span className="drawOla-file-list__item-name" title={d.name}>
                {d.name}
              </span>
              <span className="drawOla-file-list__item-size">
                {formatMB(d.sizeBytes ?? 0)}
              </span>
              <span className="drawOla-file-list__item-actions">
                <button
                  type="button"
                  className="drawOla-file-list__item-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRename(d.id, d.name);
                  }}
                  title={t("files.renamePromptLabel")}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className="drawOla-file-list__item-action drawOla-file-list__item-action--danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(d.id, d.name);
                  }}
                  title={t("files.deleteTitle")}
                >
                  ✕
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="drawOla-file-list__meter-wrap">
        <StorageMeter snapshot={snapshot} />
      </div>
    </div>
  );
};
